import React, { useState, useEffect, useMemo } from "react";
import { message, Alert, Checkbox, Statistic, Row, Col } from "antd";
import CriticalActionModal from "./CriticalActionModal";
import { rollbackInfractionPayment } from "../api/infractions/infractionRequest";

const fmt = (v) =>
  v === null || v === undefined || isNaN(Number(v))
    ? "—"
    : `${Number(v).toFixed(2)} DH`;

/**
 * Feature 2 — Roll back a paid infraction (paid → pending).
 * Soft-deletes the payment & transaction and (optionally) deducts the collected
 * amount from the collecting agent's balance. Shows the agent's current balance,
 * the projected balance, and a prominent banner if it would go negative.
 */
const RollbackInfractionPaymentModal = ({ visible, onCancel, onSuccess, infraction }) => {
  const [loading, setLoading] = useState(false);
  const [updateAgentSolde, setUpdateAgentSolde] = useState(true);

  useEffect(() => {
    if (visible) setUpdateAgentSolde(true);
  }, [visible]);

  const payment = infraction?.payment || null;
  const agent = payment?.user || null;
  const amount = Number(payment?.amount ?? infraction?.amount ?? 0);
  const currentBalance =
    agent && agent.balance !== undefined && agent.balance !== null
      ? Number(agent.balance)
      : null;

  const projectedBalance = useMemo(() => {
    if (currentBalance === null || !updateAgentSolde) return currentBalance;
    return Number((currentBalance - amount).toFixed(2));
  }, [currentBalance, amount, updateAgentSolde]);

  const willGoNegative =
    updateAgentSolde && projectedBalance !== null && projectedBalance < 0;

  const handleConfirm = async (reason) => {
    if (!infraction) return;
    setLoading(true);
    try {
      const response = await rollbackInfractionPayment(infraction.id, {
        reason,
        update_agent_solde: updateAgentSolde,
      });
      if (response?.success) {
        message.success(response.message || "Paiement annulé avec succès");
        onSuccess?.(response.data);
      } else {
        message.error(response?.message || response?.error || "Erreur lors de l'annulation");
      }
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message || "Erreur lors de l'annulation du paiement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CriticalActionModal
      visible={visible}
      onCancel={onCancel}
      onConfirm={handleConfirm}
      loading={loading}
      danger
      title="Annuler le paiement de l'infraction"
      okText="Annuler le paiement"
      confirmPhrase={infraction?.reference || (infraction?.id ? `#${infraction.id}` : null)}
      summaryItems={[
        { label: "Infraction", to: infraction?.reference || `#${infraction?.id ?? ""}` },
        { label: "Statut", from: "Payé", to: "En attente" },
        { label: "Montant", to: fmt(amount) },
      ]}
      warningBanner={
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
            message="Action irréversible côté métier"
            description="Le paiement et la transaction associés seront supprimés et l'infraction repassera en attente."
          />
          {willGoNegative && (
            <Alert
              type="error"
              showIcon
              style={{ marginBottom: 8 }}
              message="Attention : solde agent négatif"
              description={`Cette action laissera l'agent avec un solde négatif de ${fmt(projectedBalance)}.`}
            />
          )}
        </>
      }
    >
      <div style={{ marginTop: 12 }}>
        <Checkbox
          checked={updateAgentSolde}
          onChange={(e) => setUpdateAgentSolde(e.target.checked)}
        >
          Ajuster le solde de l'agent (déduire {fmt(amount)})
        </Checkbox>

        {currentBalance !== null ? (
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={12}>
              <Statistic title="Solde actuel de l'agent" value={currentBalance} precision={2} suffix="DH" />
            </Col>
            <Col span={12}>
              <Statistic
                title="Solde après l'action"
                value={projectedBalance ?? currentBalance}
                precision={2}
                suffix="DH"
                valueStyle={{ color: willGoNegative ? "#cf1322" : undefined }}
              />
            </Col>
          </Row>
        ) : (
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message="Solde de l'agent indisponible dans ce contexte ; l'ajustement sera appliqué côté serveur si l'option est cochée."
          />
        )}
      </div>
    </CriticalActionModal>
  );
};

export default RollbackInfractionPaymentModal;
